import { makeStyles } from "@mui/styles";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, Typography, Paper } from "@mui/material";
import {
	getCodeDescriptionById,
	StatusBadge,
} from "../../utils/functions";
import AddEditChecklistItemDialog from "./AddEditChecklistItemDialog";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { notify } from "../../reducers/NotifierReducer";
import {
	getChecklistItems,
	createChecklistItem,
	updateChecklistItem,
	toggleChecklistItemActive,
	reorderChecklistItems,
	deleteChecklistItem,
} from "../../reducers/ChecklistItemsReducer";

import CodeTable from "../Codes/CodeTable";

const InspectionSetupTabs = {
	ITEMS: "items",
	TYPES: "types",
};

const Tabs = [
	{
		id: InspectionSetupTabs.ITEMS,
		label: "☑️ Checklist Items",
	},
	{
		id: InspectionSetupTabs.TYPES,
		label: "⚙️ Inspection Types",
	},
];

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const route = {
	serviceName: "code-per-inspection-types",
	stateName: "inspectionTypesCodes",
	tableName: "Inspection Types Codes",
	columnOrderKey: "inspectionTypesCodeTableColumnOrder",
	columnSizeKey: "inspectionTypesCodeTableColumnSizes",
	columnVisibilityKey: "inspectionTypesCodeTableColumnVisibility",
	tableSortModelKey: "inspectionTypesCodeTableSortModel",
	title: "Inspection Types",
};

const InspectionSetup = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [clTab, setClTab] = useState(InspectionSetupTabs.ITEMS);

	const { checklistItems } = useSelector(
		(state) =>
			state.checklistItems || { checklistItems: [], loading: false },
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const [draggedId, setDraggedId] = useState(null);
	const [dragOverId, setDragOverId] = useState(null);
	const [dragPos, setDragPos] = useState("bottom");
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);

	const inspectionTypeOptions = dictionary.codePerInspectionTypes.map(
		(item) => ({
			label: item.Description,
			value: item.ptsCodeId,
			code: item.Code,
			description: item.Description,
			isDefault: item.IsDefault,
			ptsCodeId: item.ptsCodeId,
		}),
	);

	// Load checklist items
	useEffect(() => {
		if (userAgency) {
			dispatch(getChecklistItems(userAgency));
		}
	}, [dispatch, userAgency]);

	const activeItems = checklistItems.filter((i) => i.isActive !== false);
	const inactiveItems = checklistItems.filter((i) => i.isActive === false);

	// Drag and drop handlers
	const handleDragStart = (e, id) => {
		setDraggedId(id);
		e.dataTransfer.effectAllowed = "move";
		const ghost = document.createElement("div");
		ghost.style.cssText = "position:absolute;top:-999px;opacity:0;";
		document.body.appendChild(ghost);
		e.dataTransfer.setDragImage(ghost, 0, 0);
		setTimeout(() => document.body.removeChild(ghost), 0);
	};

	const handleDragOver = (e, id) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		if (id === draggedId) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const mid = rect.top + rect.height / 2;
		setDragOverId(id);
		setDragPos(e.clientY < mid ? "top" : "bottom");
	};

	const handleDragLeave = () => {
		setDragOverId(null);
	};

	const handleDrop = (e, targetId) => {
		e.preventDefault();
		if (!draggedId || draggedId === targetId) {
			setDraggedId(null);
			setDragOverId(null);
			return;
		}

		const fromIdx = checklistItems.findIndex((i) => i.id === draggedId);
		const toIdx = checklistItems.findIndex((i) => i.id === targetId);
		const [moved] = checklistItems.splice(fromIdx, 1);
		const insertAt =
			dragPos === "top" ? toIdx : toIdx + (fromIdx < toIdx ? 0 : 1);
		checklistItems.splice(
			Math.max(0, insertAt > fromIdx ? insertAt - 1 : insertAt),
			0,
			moved,
		);

		const reordered = checklistItems.map((item, i) => ({
			...item,
			sortOrder: i + 1,
		}));

		dispatch(reorderChecklistItems(reordered, userData?.user?.ptsUserID));

		setDraggedId(null);
		setDragOverId(null);
	};

	const handleDragEnd = () => {
		setDraggedId(null);
		setDragOverId(null);
	};

	// CRUD handlers
	const handleAddItem = () => {
		setSelectedItem(null);
		setOpenDialog(true);
	};

	const handleEditItem = (item) => {
		setSelectedItem(item);
		setOpenDialog(true);
	};

	const handleSaveItem = async (data) => {
		try {
			// data.inspectionTypes is now an array of values
			const inspectionTypeIds = data.inspectionTypes || [];

			if (selectedItem) {
				await dispatch(
					updateChecklistItem(
						selectedItem.id,
						{
							name: data.name,
							description: data.description,
						},
						inspectionTypeIds,
						userAgency,
						userData?.user?.ptsUserID,
						selectedItem,
					),
				);
			} else {
				await dispatch(
					createChecklistItem(
						{
							name: data.name,
							description: data.description,
						},
						inspectionTypeIds,
						userAgency,
						userData?.user?.ptsUserID,
					),
				);
			}

			setOpenDialog(false);
			setSelectedItem(null);
			// Refresh list
			dispatch(getChecklistItems(userAgency));
			dispatch(notify("Checklist item saved successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to save checklist item", "error"));
		}
	};

	const handleToggleActive = async (item) => {
		try {
			await dispatch(
				toggleChecklistItemActive(
					item.id,
					!item.isActive,
					userData?.user?.ptsUserID,
					userAgency,
					item,
				),
			);
			dispatch(
				notify(
					!item.isActive
						? "Checklist item enabled"
						: "Checklist item disabled",
					"success",
				),
			);
		} catch (error) {
			dispatch(notify("Failed to update checklist item", "error"));
		}
	};

	const handleDeleteItem = async () => {
		if (itemToDelete) {
			try {
				await dispatch(
					deleteChecklistItem(
						itemToDelete.id,
						userData?.user?.ptsUserID,
						userAgency,
						itemToDelete,
					),
				);
				setOpenDeleteDialog(false);
				setItemToDelete(null);
				dispatch(
					notify("Checklist item deleted successfully", "success"),
				);
			} catch (error) {
				dispatch(notify("Failed to delete checklist item", "error"));
			}
		}
	};

	const DragHandle = () => (
		<div className="cl-drag-handle" title="Drag to reorder">
			<div className="cl-drag-dots">
				{[...Array(6)].map((_, i) => (
					<div key={i} className="cl-drag-dot" />
				))}
			</div>
		</div>
	);

	return (
		<>
			<Box sx={{ p: 2 }}>
				<Typography variant="h5" className={classes.sideMenuTitle}>
					Inspection Setup
				</Typography>

				{/* Tabs */}
				<Box
					sx={{
						display: "flex",
						gap: 0,
						borderBottom: "1px solid var(--border)",
						marginBottom: 3,
					}}
				>
					{Tabs.map((t) => (
						<button
							key={t.id}
							onClick={() => setClTab(t.id)}
							style={{
								padding: "12px 24px",
								fontSize: 16,
								fontWeight: 600,
								border: "none",
								background: "none",
								cursor: "pointer",
								color:
									clTab === t.id
										? "var(--accent)"
										: "var(--text2)",
								borderBottom:
									clTab === t.id
										? "2px solid var(--accent)"
										: "2px solid transparent",
								marginBottom: -1,
								transition: "color 0.15s",
							}}
						>
							{t.label}
						</button>
					))}
				</Box>

				{clTab === InspectionSetupTabs.ITEMS && (
					<>
						{/* Stats */}
						<Box
							sx={{
								display: "flex",
								gap: 2,
								mb: 3,
							}}
						>
							<Paper
								className={classes.cardBackground}
								sx={{
									p: 2,
									flex: 1,
									textAlign: "center",
								}}
							>
								<Typography
									variant="h5"
									fontWeight={700}
									className={classes.colorTitle}
								>
									{checklistItems.length}
								</Typography>
								<Typography
									variant="caption"
									className={classes.colorTitle}
								>
									Total Items
								</Typography>
							</Paper>
							<Paper
								className={classes.cardBackground}
								sx={{
									p: 2,
									flex: 1,
									textAlign: "center",
								}}
							>
								<Typography
									variant="h5"
									fontWeight={700}
									color="success.main"
								>
									{activeItems.length}
								</Typography>
								<Typography
									variant="caption"
									className={classes.colorTitle}
								>
									Active
								</Typography>
							</Paper>
							<Paper
								className={classes.cardBackground}
								sx={{
									p: 2,
									flex: 1,
									textAlign: "center",
								}}
							>
								<Typography
									variant="h5"
									fontWeight={700}
									color="text.disabled"
								>
									{inactiveItems.length}
								</Typography>
								<Typography
									variant="caption"
									className={classes.colorTitle}
								>
									Disabled
								</Typography>
							</Paper>
						</Box>

						<div className="section-actions mb-2">
							<Box sx={{ mb: 3 }}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleAddItem}
								>
									+ Add Checklist Item
								</Button>
							</Box>
						</div>

						{/* Active items */}
						<Box sx={{ mb: 2 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 1,
								}}
							>
								<Typography
									variant="subtitle1"
									fontWeight={600}
									className={classes.colorTitle}
								>
									Active Items ({activeItems.length})
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									Drag handles to reorder · order applies to
									all future inspections
								</Typography>
							</Box>

							<Paper
								variant="outlined"
								sx={{
									p: activeItems.length === 0 ? 4 : 0,
								}}
							>
								{activeItems.length === 0 && (
									<Box
										sx={{
											textAlign: "center",
											py: 4,
										}}
									>
										<Typography variant="h4" sx={{ mb: 1 }}>
											☑️
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											No active checklist items
										</Typography>
									</Box>
								)}

								{activeItems.map((item, idx) => {
									const isDragging = draggedId === item.id;
									const isOver = dragOverId === item.id;
									const rowClass = [
										"cl-item-row",
										isDragging ? "dragging" : "",
										isOver && dragPos === "top"
											? "drag-over-top"
											: "",
										isOver && dragPos === "bottom"
											? "drag-over-bottom"
											: "",
									]
										.filter(Boolean)
										.join(" ");

									return (
										<Box
											key={item.id}
											className={`${rowClass} ${classes.colorTitle}`}
											draggable
											onDragStart={(e) =>
												handleDragStart(e, item.id)
											}
											onDragOver={(e) =>
												handleDragOver(e, item.id)
											}
											onDragLeave={handleDragLeave}
											onDrop={(e) =>
												handleDrop(e, item.id)
											}
											onDragEnd={handleDragEnd}
											sx={{
												display: "flex",
												alignItems: "center",
												py: 1.5,
												px: 2,
												borderBottom:
													idx < activeItems.length - 1
														? "1px solid var(--border)"
														: "none",
												"&:hover": {
													bgcolor: "action.hover",
												},
											}}
										>
											<DragHandle />
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													flex: 1,
													minWidth: 0,
												}}
											>
												<span
													className="cl-sort-badge"
													style={{
														marginRight: 12,
														fontWeight: 600,
														color: "var(--text3)",
													}}
												>
													{idx + 1}
												</span>
												<Box sx={{ minWidth: 0 }}>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														sx={{ mb: 0.25 }}
													>
														{item.name}
													</Typography>
													{item.description && (
														<Typography
															variant="caption"
															color="text.secondary"
															noWrap
														>
															{item.description}
														</Typography>
													)}
													<Box
														sx={{
															display: "flex",
															flexWrap: "wrap",
															gap: 0.5,
															mt: 0.5,
														}}
													>
														{!item.inspectionTypes ||
														item.inspectionTypes
															.length === 0 ? (
															<Typography
																variant="caption"
																color="text.disabled"
																fontStyle="italic"
															>
																All inspection
																types
															</Typography>
														) : (
															item.inspectionTypes.map(
																(t) => (
																	<Typography
																		key={t}
																		variant="caption"
																		sx={{
																			fontWeight: 600,
																			px: 1,
																			py: 0.25,
																			borderRadius: 20,
																			bgcolor:
																				"rgba(245,166,35,0.12)",
																			color: "var(--accent)",
																			border: "1px solid rgba(245,166,35,0.3)",
																		}}
																	>
																		{getCodeDescriptionById(
																			t,
																			dictionary.codePerInspectionTypes,
																		)}
																	</Typography>
																),
															)
														)}
													</Box>
												</Box>
											</Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													ml: 2,
												}}
											>
												<StatusBadge status="Active" />
											</Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													ml: 2,
													gap: 1,
												}}
											>
												<Button
													size="small"
													onClick={() =>
														handleEditItem(item)
													}
												>
													Edit
												</Button>
												<Button
													size="small"
													onClick={() =>
														handleToggleActive(item)
													}
												>
													Disable
												</Button>
												<Button
													size="small"
													color="error"
													onClick={() => {
														setItemToDelete(item);
														setOpenDeleteDialog(
															true,
														);
													}}
												>
													Remove
												</Button>
											</Box>
										</Box>
									);
								})}
							</Paper>
						</Box>

						{/* Inactive items */}
						{inactiveItems.length > 0 && (
							<Box>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										mb: 1,
									}}
								>
									<Typography
										variant="subtitle1"
										fontWeight={600}
										className={classes.colorTitle}
										sx={{ fontSize: 13 }}
									>
										Disabled Items ({inactiveItems.length})
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
									>
										Not included in new inspections — can be
										re-enabled
									</Typography>
								</Box>

								<Paper variant="outlined">
									{inactiveItems.map((item) => (
										<Box
											key={item.id}
											className="cl-item-row inactive"
											sx={{
												display: "flex",
												alignItems: "center",
												py: 1.5,
												px: 2,
												opacity: 0.6,
											}}
										>
											<Box sx={{ width: 28 }} />
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													flex: 1,
													minWidth: 0,
												}}
											>
												<span
													className="cl-sort-badge"
													style={{
														marginRight: 12,
														color: "var(--text3)",
													}}
												>
													—
												</span>
												<Box sx={{ minWidth: 0 }}>
													<Typography
														variant="body2"
														fontWeight={600}
														noWrap
														className={
															classes.colorTitle
														}
													>
														{item.name}
													</Typography>
													{item.description && (
														<Typography
															variant="caption"
															color="text.secondary"
															noWrap
														>
															{item.description}
														</Typography>
													)}
												</Box>
											</Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													ml: 2,
												}}
											>
												<StatusBadge status="Inactive" />
											</Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													ml: 2,
													gap: 1,
												}}
											>
												<Button
													size="small"
													onClick={() =>
														handleToggleActive(item)
													}
												>
													Re-enable
												</Button>
												<Button
													size="small"
													color="error"
													onClick={() => {
														setItemToDelete(item);
														setOpenDeleteDialog(
															true,
														);
													}}
												>
													Remove
												</Button>
											</Box>
										</Box>
									))}
								</Paper>
							</Box>
						)}
					</>
				)}

				{clTab === InspectionSetupTabs.TYPES && (
					<CodeTable
						serviceName={route.serviceName}
						stateName={route.stateName}
						tableName={route.tableName}
						columnOrderKey={route.columnOrderKey}
						columnSizeKey={route.columnSizeKey}
						columnVisibilityKey={route.columnVisibilityKey}
						tableSortModelKey={route.tableSortModelKey}
						title={route.title}
					/>
				)}
			</Box>

			<AddEditChecklistItemDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedItem(null);
				}}
				onSave={handleSaveItem}
				initialData={selectedItem}
				viewOnly={false}
				inspectionTypeOptions={inspectionTypeOptions}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setItemToDelete(null);
				}}
				onConfirm={handleDeleteItem}
				title="Delete Checklist Item"
				message="Are you sure you want to delete this checklist item?"
			/>
		</>
	);
};

export default InspectionSetup;
